import { useEffect, useState } from 'react';
import { type PendingRedemption } from '@chainflip/sdk/funding';
import { useMutation } from '@tanstack/react-query';
import { formatDistanceToNow, fromUnixTime, isAfter } from 'date-fns';
import { useAccount } from 'wagmi';
import Countdown from '@/shared/components/Countdown';
import Button from '@/shared/components/flip-ui-kit/Button';
import { type CastCacheValidatorFragment } from '@/shared/graphql/cache/fragments';
import useInterval from '@/shared/hooks/useInterval';
import useOldFundingSDK from '@/shared/hooks/useOldFundingSdk';
import useToast from '@/shared/hooks/useToast';
import useTracking from '@/shared/hooks/useTracking';
import ClockIcon from '@/shared/icons/Clock';
import { addToGoogleCalendar } from '@/shared/utils/date';
import { FLIP_SYMBOL } from '@/shared/utils/env';
import { tryGetEthersError } from '@/shared/utils/functions';
import TokenAmount from '@/shared/utils/TokenAmount';
import ResumeDisclaimer from './ResumeDisclaimer';
import CalenderIcon from '../../../../assets/svg/socials/googlecalender.svg';
import { StakeEvents, type StakeTrackEvents } from '../../../../types/track';

type Step2Props = {
  validator: Omit<CastCacheValidatorFragment, 'totalBalance'>;
  pendingRedemption: PendingRedemption;
  onCancel: () => void;
  onComplete: () => void;
};

const Step2 = ({ validator, pendingRedemption, onCancel, onComplete }: Step2Props) => {
  const track = useTracking<StakeTrackEvents>();
  const toast = useToast('redemption');
  const { address: connectedAddress } = useAccount();
  const { fundingSDK, redemptionDelayInMilliseconds } = useOldFundingSDK();

  const [now, setNow] = useState(() => Date.now());

  const executableRedemptionStartTimestamp = fromUnixTime(Number(pendingRedemption.startTime));

  const redemptionDelayInWords = redemptionDelayInMilliseconds
    ? formatDistanceToNow(Date.now() + redemptionDelayInMilliseconds)
    : '2 days';

  useInterval(() => {
    setNow(Date.now());
  }, 1000);

  const isStartTimeElapsed = isAfter(now, executableRedemptionStartTimestamp);

  useEffect(() => {
    if (!isStartTimeElapsed) {
      track(StakeEvents.CommandSuccess, {
        props: {
          executeStartTime: executableRedemptionStartTimestamp.toUTCString(),
          connectedWallet: connectedAddress as string,
          scAccount: validator.idSs58,
          path: window.location.pathname,
          position: 'redeem_modal',
          redeemAddress: pendingRedemption.redeemAddress,
          redeemAmount: new TokenAmount(pendingRedemption.amount).toFixed(),
        },
      });
    }
  }, [isStartTimeElapsed]);

  const executableRedemptionExpiryTime = fromUnixTime(Number(pendingRedemption.expiryTime));
  const isExpiryTimeElapsed = isAfter(now, executableRedemptionExpiryTime);

  const { mutate: executeRedemption, isPending: isExecutingRedemption } = useMutation({
    mutationFn: async (_: React.MouseEvent) => {
      track(isExpiryTimeElapsed ? StakeEvents.CtaRefund : StakeEvents.CtaRedeem, {
        props: {
          expiryTime: executableRedemptionExpiryTime.toUTCString(),
          connectedWallet: connectedAddress as string,
          scAccount: validator.idSs58,
          path: window.location.pathname,
          position: 'redeem_modal',
          redeemAddress: pendingRedemption.redeemAddress,
          redeemAmount: new TokenAmount(pendingRedemption.amount).toFixed(),
        },
      });

      toast.loading('Executing redemption');

      await fundingSDK!.executeRedemption(validator.idHex as `0x${string}`);
    },
    onError(error) {
      toast.error('Failed to execute redemption', {
        description: tryGetEthersError(error, 'Redemption execution'),
      });
    },
    onSuccess() {
      toast.success(`Redemption executed`);

      if (!isExpiryTimeElapsed) {
        track(StakeEvents.RedeemSuccess, {
          props: {
            connectedWallet: connectedAddress as string,
            path: window.location.pathname,
            position: 'redeem_modal',
            redeemAddress: pendingRedemption.redeemAddress,
            redeemAmount: new TokenAmount(pendingRedemption.amount).toFixed(),
          },
        });
      }

      onComplete();
      onCancel();
    },
  });

  return (
    <div className="flex flex-col space-y-12">
      {!isStartTimeElapsed && (
        <>
          <div className="space-y-2">
            <p className="w-full font-aeonikMedium text-24 text-cf-white">Waiting to redeem</p>
            <p className="text-14">
              A waiting period of {redemptionDelayInWords} is required before redeeming funds to
              limit the risk in Chainflip&apos;s vaults.
            </p>
          </div>
          <div className="flex flex-col items-center justify-center space-y-4">
            <button
              type="button"
              className="mt-4 flex items-center justify-center rounded-md bg-cf-gray-3 px-4 py-2 text-12"
              onClick={() =>
                addToGoogleCalendar(
                  pendingRedemption.amount,
                  executableRedemptionStartTimestamp,
                  executableRedemptionExpiryTime,
                )
              }
            >
              <span className="px-2">
                <CalenderIcon />
              </span>
              Add to Google Calendar
            </button>
            <Countdown now={now} timestamp={executableRedemptionStartTimestamp} />
          </div>
        </>
      )}
      {isStartTimeElapsed && !isExpiryTimeElapsed && (
        <>
          <div className="space-y-2 text-left">
            <p className="font-aeonikMedium text-24 text-cf-white">Redeem your funds</p>
            <p className="text-14">
              You are all set! Redeem your {FLIP_SYMBOL} before the expiration time, otherwise it
              will be credited back to your Validator balance.
            </p>
          </div>
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="flex items-center space-x-2 rounded-md bg-cf-orange-4 px-4 py-1 text-cf-orange-1">
              <ClockIcon width="18" height="18" />
              <span className="text-14 text-cf-orange-1">Your redemption expires in:</span>
            </div>
            <Countdown now={now} timestamp={executableRedemptionExpiryTime} />
          </div>
        </>
      )}
      {isExpiryTimeElapsed && (
        <>
          <div className="space-y-2">
            <p className="font-aeonikMedium text-24 text-cf-white">Redemption expired</p>
            <p className="text-14">
              Your redemption has expired. You must first refund the {FLIP_SYMBOL} to your state
              chain account before starting the redemption process again.
            </p>
          </div>
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="flex items-center space-x-2 rounded-md border border-cf-red-1/20 bg-cf-red-4 px-4 py-1 text-cf-red-1">
              <ClockIcon width="18" height="18" />
              <span className="text-14">
                Your redemption expired on {executableRedemptionExpiryTime.toLocaleDateString()} at{' '}
                {executableRedemptionExpiryTime.toLocaleTimeString()}
              </span>
            </div>
            <Countdown duration={{}} />
          </div>
        </>
      )}
      <div className="flex items-center text-cf-light-1">
        <ResumeDisclaimer />
        <Button
          disabled={!isStartTimeElapsed || isExecutingRedemption}
          className="ml-auto justify-center"
          onClick={executeRedemption}
          loading={isExecutingRedemption}
        >
          <div className="flex items-center whitespace-nowrap">
            {!isExpiryTimeElapsed ? 'Redeem' : `Refund ${FLIP_SYMBOL} to your balance`}
          </div>
        </Button>
      </div>
    </div>
  );
};

export default Step2;
